import React, { useState, useEffect, useMemo } from 'react'
import { Checkbox } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { PageHeader, LoaderBar } from 'components'
import styled from 'styled-components'
import { useAuth } from 'context'
import { GetAuditCompanyList } from 'hooks'
import { GetMenuList } from 'hooks/menu-setting'
import { PermissionSettingService } from 'utility/services/permission'
import { MenuSettingService } from 'utility/services/menu-setting'
import { useNotification } from 'hooks/toast'
import {
    Scroll,
    GearSix,
    Users,
    UsersThree,
    ListChecks,
    ClipboardText,
    Ticket,
    FileText,
    Package,
    Key,
    SquaresFour,
    CaretDown,
    CaretRight,
} from 'phosphor-react'

const Container = styled.div`
    padding: 10px 0;
`

const SectionCard = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #fff;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`

const CardHeader = styled.div`
    background-color: #f8f9fa;
    padding: 12px 18px;
    font-weight: 600;
    font-size: 15px;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
`

const CardBody = styled.div`
    padding: 18px;
`

const ModuleRowContainer = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 16px;
    overflow: hidden;
`

const ModuleHeaderRow = styled.div`
    padding: 14px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #fff;
    cursor: pointer;
    transition: background-color 0.2s ease;
    &:hover {
        background-color: #f9fafb;
    }
`

const IconContainer = styled.div`
    width: 42px;
    height: 42px;
    background-color: #e3f2fd;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: bold;
    color: #1565c0;
    margin-right: 16px;
    flex-shrink: 0;
`

const SubRow = styled.div`
    padding: 12px 18px;
    background-color: #fff;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    &:last-child {
        border-bottom: none;
    }
`

const SubHeader = styled.div`
    background-color: #f8f9fa;
    padding: 8px 18px;
    font-size: 11px;
    font-weight: 600;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #e5e7eb;
`

const getMenuIcon = (iconKey, title, size = 22) => {
    if (title === 'Invoice' || title === 'Invoice Settings') {
        return <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1565c0' }}>$</span>
    }
    if (title === 'Audit' || title === 'Report') {
        return <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1565c0' }}>A</span>
    }
    const ICON_MAP = {
        templates: Scroll,
        report: GearSix,
        'report-item': GearSix,
        customer: Users,
        group: UsersThree,
        task: ListChecks,
        'action-plan': ClipboardText,
        settings: GearSix,
        support: Ticket,
        invoice: FileText,
        'invoice-item': FileText,
        asset: Package,
        key: Key,
    }
    const IconComponent = ICON_MAP[iconKey] || SquaresFour
    return <IconComponent size={size} weight="bold" />
}

const MODULE_DESCRIPTIONS = {
    'menu.invoice': 'Generate invoices, view history, audit completions.',
    'menu.report': 'Daily inspections, scoring, NC reports.',
    'menu.dashboard': 'Operational charts. Single-page module.',
    'menu.templates': 'Manage document and checklist templates.',
    'menu.customer': 'Manage customers and clients.',
    'menu.group': 'Manage user groups and organizational units.',
    'menu.task': 'Track and assign tasks.',
    'menu.settings': 'General system settings and configurations.',
    'menu.support': 'Support tickets and assistance.',
}

export const CompanyMenuPermissionScreen = () => {
    const { user: currentUser } = useAuth()
    const { successToast, errorToast } = useNotification()

    const [selectedCompanyId, setSelectedCompanyId] = useState(null)
    const [searchText, setSearchText] = useState('')
    const [companyPermissions, setCompanyPermissions] = useState([])
    const [loadingPermissions, setLoadingPermissions] = useState(false)
    const [expandedModules, setExpandedModules] = useState({})
    const [modulePermissionsMap, setModulePermissionsMap] = useState({})
    const [loadingModuleId, setLoadingModuleId] = useState(null)

    const isSuperAdmin = currentUser?.IsSuperAdmin

    const {
        data: companies,
        loading: loadingCompanies,
        filterChanged,
    } = GetAuditCompanyList(isSuperAdmin, true, 50)

    const menuParams = useMemo(() => ({ onlyActive: true, onlyVisible: true }), [])
    const { data: menuList, loading: loadingMenuList } = GetMenuList(menuParams)

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            filterChanged({ search: searchText })
        }, 500)

        return () => clearTimeout(delayDebounceFn)
    }, [searchText, filterChanged])

    const companyOptions = useMemo(() => {
        if (!companies) return []
        return companies.map(ele => ({
            key: ele.CompanyId,
            value: ele.CompanyId,
            text: `${ele.CompanyName || ele.Name} (CompanyId ${ele.CompanyId})`,
        }))
    }, [companies])

    const rootMenus = useMemo(() => {
        if (!menuList) return []
        return menuList.filter(menu => menu.ParentMenuId === null)
    }, [menuList])

    const fetchCompanyPermissions = async (companyId, showLoader = true) => {
        if (showLoader) {
            setLoadingPermissions(true)
        }
        try {
            const res = await PermissionSettingService.getCompanyPermissions(companyId)
            const perms = Array.isArray(res) ? res : (res?.data || [])
            setCompanyPermissions(perms)
        } catch (error) {
            console.error('Error loading permissions:', error)
            errorToast('Failed to load company permissions.')
        } finally {
            if (showLoader) {
                setLoadingPermissions(false)
            }
        }
    }

    useEffect(() => {
        if (selectedCompanyId) {
            fetchCompanyPermissions(selectedCompanyId)
        } else {
            setCompanyPermissions([])
        }
    }, [selectedCompanyId])

    const loadModuleDetails = async (rootMenu) => {
        const subMenus = menuList.filter(m => m.ParentMenuId === rootMenu.MenuId)
        const allMenuIds = [rootMenu.MenuId, ...subMenus.map(m => m.MenuId)]

        setLoadingModuleId(rootMenu.MenuId)
        try {
            // 1. Fetch mapped permissions for each menu item
            const mappings = await Promise.all(
                allMenuIds.map(async (menuId) => {
                    try {
                        const res = await MenuSettingService.menuPermission(menuId)
                        return { menuId, permissions: Array.isArray(res) ? res : [] }
                    } catch {
                        return { menuId, permissions: [] }
                    }
                }),
            )

            // 2. Extract and partition permissions
            const allPerms = []
            const seenIds = new Set()
            mappings.forEach(m => {
                m.permissions.forEach(p => {
                    if (!seenIds.has(p.PermissionId)) {
                        seenIds.add(p.PermissionId)
                        allPerms.push(p)
                    }
                })
            })

            const rootMenuViewKey = `${rootMenu.MenuKey}.view`.toLowerCase()
            const menuVisPerms = allPerms.filter(p => {
                const key = (p.PermissionKey || '').toLowerCase()
                return key.startsWith('menu.') && key !== rootMenuViewKey
            })

            const actionsDataPerms = allPerms.filter(p => {
                const key = (p.PermissionKey || '').toLowerCase()
                return !key.startsWith('menu.')
            })

            setModulePermissionsMap(prev => ({
                ...prev,
                [rootMenu.MenuId]: {
                    menuVisibility: menuVisPerms,
                    actionsData: actionsDataPerms,
                    loaded: true,
                },
            }))
        } catch (error) {
            console.error('Error loading module details:', error)
            errorToast('Failed to load module details.')
        } finally {
            setLoadingModuleId(null)
        }
    }

    const toggleExpand = (menu) => {
        const menuId = menu.MenuId
        const isExpanded = !expandedModules[menuId]
        setExpandedModules(prev => ({ ...prev, [menuId]: isExpanded }))

        const details = modulePermissionsMap[menuId]
        if (isExpanded && (!details || !details.loaded)) {
            loadModuleDetails(menu)
        }
    }

    const handleToggleModule = async (moduleKey, isEnabled) => {
        if (!selectedCompanyId) return

        // Find corresponding menu and its sub-permissions
        const menu = menuList?.find(m => {
            const key = m.MenuKey.startsWith('menu.') ? m.MenuKey.slice(5) : m.MenuKey
            return key === moduleKey
        })
        if (!menu) return

        const details = modulePermissionsMap[menu.MenuId]
        const subPerms = details ? [
            ...(details.menuVisibility || []),
            ...(details.actionsData || []),
        ] : []

        // Optimistically update the state
        const menuKey = menu.MenuKey || ''
        const expectedKey = `${menuKey}.view`.toLowerCase()
        setCompanyPermissions(prev => {
            if (isEnabled) {
                const nextPerms = [...prev]
                if (!nextPerms.some(p => p.PermissionKey?.toLowerCase() === expectedKey)) {
                    nextPerms.push({ PermissionId: `temp-view-${menu.MenuId}`, PermissionKey: expectedKey })
                }
                subPerms.forEach(sp => {
                    if (!nextPerms.some(p => p.PermissionId === sp.PermissionId)) {
                        nextPerms.push(sp)
                    }
                })
                return nextPerms
            }
            let nextPerms = prev.filter(p => p.PermissionKey?.toLowerCase() !== expectedKey)
            const subPermIds = new Set(subPerms.map(sp => sp.PermissionId))
            nextPerms = nextPerms.filter(p => !subPermIds.has(p.PermissionId))
            return nextPerms
        })

        try {
            // Call the module endpoint to inform the backend of the module toggle
            await PermissionSettingService.toggleCompanyModule(selectedCompanyId, moduleKey, isEnabled)

            successToast(`Module "${menu.Title}" ${isEnabled ? 'enabled' : 'disabled'} successfully.`)
            await fetchCompanyPermissions(selectedCompanyId, false)
        } catch (error) {
            console.error('Error toggling module:', error)
            errorToast(`Failed to update module "${menu.Title}".`)
            // Rollback optimistic update
            await fetchCompanyPermissions(selectedCompanyId, false)
        }
    }

    const handleTogglePermission = async (permission, isEnabled) => {
        if (!selectedCompanyId) return

        // Optimistically update the state
        setCompanyPermissions(prev => {
            if (isEnabled) {
                if (!prev.some(p => p.PermissionId === permission.PermissionId)) {
                    return [...prev, permission]
                }
                return prev
            }
            return prev.filter(p => p.PermissionId !== permission.PermissionId)
        })

        try {
            await PermissionSettingService.saveCompanyPermission(selectedCompanyId, permission.PermissionId, isEnabled)
            successToast(`Permission "${permission.PermissionName}" updated successfully.`)
            await fetchCompanyPermissions(selectedCompanyId, false)
        } catch (error) {
            console.error('Error toggling permission:', error)
            errorToast(`Failed to update permission "${permission.PermissionName}".`)
            // Rollback optimistic update
            await fetchCompanyPermissions(selectedCompanyId, false)
        }
    }

    const isModuleEnabled = (menu) => {
        const menuKey = menu.MenuKey || ''
        const keysToCheck = new Set([
            `${menuKey}.view`.toLowerCase(),
            menuKey.toLowerCase(),
        ])
        if (menuKey.toLowerCase().startsWith('menu.')) {
            const stripped = menuKey.slice(5)
            keysToCheck.add(`${stripped}.view`.toLowerCase())
            keysToCheck.add(stripped.toLowerCase())
        }
        return companyPermissions.some(p => p.PermissionKey && keysToCheck.has(p.PermissionKey.toLowerCase()))
    }

    const getModuleStatusText = (menu) => {
        const details = modulePermissionsMap[menu.MenuId]
        if (!details || !details.loaded) {
            return isModuleEnabled(menu) ? 'All sub-menus & actions enabled' : 'All sub-menus & actions disabled'
        }
        const total = (details.menuVisibility?.length || 0) + (details.actionsData?.length || 0)
        if (total === 0) {
            return isModuleEnabled(menu) ? 'All sub-menus & actions enabled' : 'All sub-menus & actions disabled'
        }
        const enabledCount = [
            ...(details.menuVisibility || []),
            ...(details.actionsData || []),
        ].filter(p => companyPermissions.some(cp => cp.PermissionId === p.PermissionId)).length

        if (enabledCount === total) {
            return 'All sub-menus & actions enabled'
        }
        if (enabledCount === 0) {
            return 'All sub-menus & actions disabled'
        }
        return `${enabledCount} of ${total} sub-menus & actions enabled`
    }

    return (
        <Container>
            <PageHeader header="Company Menu Permission" hideWeb />

            {/* Pick a Company Card */}
            <SectionCard>
                <CardHeader>Pick a company</CardHeader>
                <CardBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#4b5563' }}>Company</label>
                        <Dropdown
                            placeholder="Select Company"
                            fluid
                            search
                            selection
                            selectOnNavigation={false}
                            selectOnBlur={false}
                            options={companyOptions}
                            value={selectedCompanyId}
                            onChange={(e, { value }) => setSelectedCompanyId(value)}
                            onSearchChange={(e, { searchQuery }) => setSearchText(searchQuery)}
                            loading={loadingCompanies}
                        />
                    </div>
                </CardBody>
            </SectionCard>

            {/* Modules available for this company Card */}
            {selectedCompanyId && (
                <SectionCard>
                    <CardHeader>Modules available for this company</CardHeader>
                    <CardBody>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                            Toggle a module on/off. Changes take effect on each user's next login.
                        </div>

                        {loadingPermissions || loadingMenuList ? (
                            <LoaderBar content="Loading modules and permission states..." />
                        ) : (
                            rootMenus.map(menu => {
                                const moduleKey = menu.MenuKey.startsWith('menu.') ? menu.MenuKey.slice(5) : menu.MenuKey
                                const description = MODULE_DESCRIPTIONS[menu.MenuKey] || `Manage settings and visibility for the ${menu.Title} module.`
                                const isEnabled = isModuleEnabled(menu)
                                const hasSubMenus = menuList ? menuList.some(m => m.ParentMenuId === menu.MenuId) : false
                                const isExpandable = isEnabled && hasSubMenus
                                const isExpanded = isExpandable && !!expandedModules[menu.MenuId]
                                const details = modulePermissionsMap[menu.MenuId]

                                let caretElement = <div style={{ width: '30px' }} />
                                if (isExpandable) {
                                    caretElement = isExpanded ? (
                                        <CaretDown size={18} style={{ marginRight: '12px', color: '#6b7280' }} />
                                    ) : (
                                        <CaretRight size={18} style={{ marginRight: '12px', color: '#6b7280' }} />
                                    )
                                }

                                return (
                                    <ModuleRowContainer key={menu.MenuId}>
                                        <ModuleHeaderRow
                                            onClick={() => isExpandable && toggleExpand(menu)}
                                            style={{ cursor: isExpandable ? 'pointer' : 'default' }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                {caretElement}
                                                <IconContainer>{getMenuIcon(menu.IconKey, menu.Title)}</IconContainer>
                                                <div>
                                                    <div style={{ fontWeight: '600', fontSize: '15px', color: '#111827', marginBottom: '2px' }}>
                                                        {menu.Title}
                                                    </div>
                                                    <div style={{ fontSize: '12.5px', color: '#6b7280' }}>
                                                        {description}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', fontWeight: '500' }}>
                                                        {getModuleStatusText(menu)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    toggle
                                                    checked={isEnabled}
                                                    onChange={(e, data) => handleToggleModule(moduleKey, data.checked)}
                                                />
                                            </div>
                                        </ModuleHeaderRow>

                                        {isExpanded && (
                                            <div style={{ borderTop: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
                                                {loadingModuleId === menu.MenuId ? (
                                                    <div style={{ padding: '16px' }}>
                                                        <LoaderBar content="Loading sub-menus and permissions..." />
                                                    </div>
                                                ) : (
                                                    <>
                                                        {details?.menuVisibility?.length > 0 && (
                                                            <>
                                                                <SubHeader>
                                                                    Menu Visibility ({details.menuVisibility.length})
                                                                </SubHeader>
                                                                {details.menuVisibility.map(perm => {
                                                                    const isPermEnabled = companyPermissions.some(cp => cp.PermissionId === perm.PermissionId)
                                                                    return (
                                                                        <SubRow key={perm.PermissionId}>
                                                                            <div>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#374151' }}>
                                                                                        {perm.PermissionName}
                                                                                    </span>
                                                                                    <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '11px', backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>
                                                                                        {perm.PermissionKey}
                                                                                    </span>
                                                                                </div>
                                                                                {perm.Description && (
                                                                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                                                                                        {perm.Description}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <Checkbox
                                                                                toggle
                                                                                checked={isPermEnabled}
                                                                                onChange={(e, data) => handleTogglePermission(perm, data.checked)}
                                                                                disabled={!isEnabled}
                                                                            />
                                                                        </SubRow>
                                                                    )
                                                                })}
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </ModuleRowContainer>
                                )
                            })
                        )}
                    </CardBody>
                </SectionCard>
            )}
        </Container>
    )
}
